import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import { parseProjectIdFromPath } from '../utils/deepLink';

const ActiveProjectContext = createContext(null);

function storageKey(userId) {
  return `testgate_active_project_${userId || 'anonymous'}`;
}

export function ActiveProjectProvider({ children }) {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectIdState] = useState('');
  const [loading, setLoading] = useState(true);

  const isClient = user?.role === 'client' || user?.role === 'client_team';
  const isStaff =
    user?.role === 'admin' ||
    user?.role === 'platform_owner' ||
    user?.role === 'platform_test_manager';
  const usesProjectShell = isClient || isStaff;

  const refreshProjects = useCallback(async () => {
    if (!usesProjectShell || !user?.id) {
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/projects', { params: { limit: 500, page: 1 } });
      const list = res.data?.projects || (Array.isArray(res.data) ? res.data : []);
      setProjects(list);

      const stored = localStorage.getItem(storageKey(user.id));
      const validStored = stored && list.some((p) => p.id === stored);
      if (validStored) {
        setActiveProjectIdState(stored);
      } else if (list.length === 1) {
        setActiveProjectIdState(list[0].id);
        localStorage.setItem(storageKey(user.id), list[0].id);
      } else {
        setActiveProjectIdState('');
        localStorage.removeItem(storageKey(user.id));
      }
    } catch (e) {
      console.warn('ActiveProjectContext load', e);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [usesProjectShell, user?.id]);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  useEffect(() => {
    if (!usesProjectShell) return;
    const fromPath = parseProjectIdFromPath(location.pathname);
    if (fromPath && fromPath !== activeProjectId) {
      setActiveProjectIdState(fromPath);
      if (user?.id) localStorage.setItem(storageKey(user.id), fromPath);
    }
  }, [location.pathname, usesProjectShell, activeProjectId, user?.id]);

  const setActiveProject = useCallback(
    (projectId) => {
      const id = projectId || '';
      setActiveProjectIdState(id);
      if (user?.id) {
        if (id) localStorage.setItem(storageKey(user.id), id);
        else localStorage.removeItem(storageKey(user.id));
      }
    },
    [user?.id]
  );

  const activeProject = useMemo(
    () => projects.find((p) => p.id === activeProjectId) || null,
    [projects, activeProjectId]
  );

  const buildScopedSearchParams = useCallback(
    (extra = {}) => {
      const sp = new URLSearchParams(extra);
      if (activeProjectId) sp.set('projectId', activeProjectId);
      return sp;
    },
    [activeProjectId]
  );

  const value = useMemo(
    () => ({
      projects,
      activeProjectId,
      activeProject,
      setActiveProject,
      refreshProjects,
      loading,
      buildScopedSearchParams,
      requiresProjectSelection:
        isClient && projects.length > 1 && !activeProjectId,
      usesProjectShell,
      isStaff,
    }),
    [
      projects,
      activeProjectId,
      activeProject,
      setActiveProject,
      refreshProjects,
      loading,
      buildScopedSearchParams,
      isClient,
      usesProjectShell,
      isStaff,
    ]
  );

  return (
    <ActiveProjectContext.Provider value={value}>{children}</ActiveProjectContext.Provider>
  );
}

// Module-level so consumers outside the provider (e.g. the tester shell) get stable
// references; a fresh object per render re-triggers their effects in a tight loop.
const FALLBACK_CONTEXT = Object.freeze({
  projects: [],
  activeProjectId: '',
  activeProject: null,
  setActiveProject: () => {},
  refreshProjects: async () => {},
  loading: false,
  buildScopedSearchParams: (extra = {}) => new URLSearchParams(extra),
  requiresProjectSelection: false,
});

export function useActiveProject() {
  const ctx = useContext(ActiveProjectContext);
  return ctx || FALLBACK_CONTEXT;
}
